import { use, type ReactNode } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { NavBarIcon, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { WebsiteTooltip } from "app/components/React/Tooltip/WebsiteTooltip/WebsiteTooltip";
import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { StyledLink } from "pro-features/styled components/StyledLink/src/StyledLink";

import {
    type GrowthOverviewGranularity,
    type GrowthOverviewMetric,
    GROWTH_OVERVIEW_QUERY_KEY,
    GROWTH_OVERVIEW_EXPANDED_ROW_HIGHLIGHT,
} from "../const";
import { GROWTH_OVERVIEW_I18N_KEYS } from "../growthOverviewTexts";
import { useGrowthOverviewDomainHref } from "../hooks/useGrowthOverviewDomainHref";
import type { IGrowthOverviewPeriodCell, IGrowthOverviewTableRow } from "../types";
import { isCategoryWeeklyUniqueUnavailable } from "../utils/categoryWeeklyUniqueUtils";
import { shouldUseLightHeatmapText, type IGrowthHeatmapStyle } from "../utils/heatmapScale";
import { isGrowthOverviewRowExpandable } from "../utils/isGrowthOverviewRowExpandable";
import { getPeriodsForMetric, getSummaryForMetric } from "../utils/mapApiRows";

import { HeatmapPercentCell } from "./cells/HeatmapPercentCell";
import { GrowthOverviewHeatmapContext } from "./GrowthOverviewHeatmapContext";
import { GrowthOverviewHighlightedText } from "./GrowthOverviewHighlightedText";
import {
    ExpandIcon,
    GrowthOverviewExpandSpinner,
    GrowthOverviewNameCell,
    GrowthOverviewNameCellButton,
    GrowthOverviewPeriodCell,
    GrowthOverviewRowLabel,
    GrowthOverviewShowAllCellButton,
    GrowthOverviewSummaryCell,
    ShowAllDomainsLabel,
} from "./styled";

const NA_DASH = "—";

const CategoryWeeklyUniqueUnavailableDash = ({ tooltip }: { tooltip: string }) => (
    <Tooltip
        placement="top"
        content={tooltip}
    >
        <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
        >
            <Text
                as="span"
                fontVariant="input2"
            >
                {NA_DASH}
            </Text>
        </Box>
    </Tooltip>
);

export interface IGrowthOverviewRowHoverProps {
    rowId: string;
    onRowMouseEnter: () => void;
    onRowMouseLeave: () => void;
}

const getRowHoverCellProps = (
    rowHover: IGrowthOverviewRowHoverProps | undefined,
    isHighlighted = false,
    rowId?: string,
): {
    "data-row-id"?: string;
    "data-row-highlighted"?: "true";
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
} => {
    if (!rowHover) {
        return {};
    }

    return {
        "data-row-id": rowId ?? rowHover.rowId,
        ...(isHighlighted ? { "data-row-highlighted": "true" as const } : {}),
        onMouseEnter: rowHover.onRowMouseEnter,
        onMouseLeave: rowHover.onRowMouseLeave,
    };
};

export interface IGrowthOverviewPeriodCellParams {
    row: IGrowthOverviewTableRow;
    periodHeaders: IGrowthOverviewPeriodCell[];
    metric: GrowthOverviewMetric;
    granularity: GrowthOverviewGranularity;
    getHeatmapColor: (pop: number | null | undefined) => string;
    getHeatmapStyle?: (pop: number | null | undefined) => IGrowthHeatmapStyle;
    searchHighlightTerm?: string;
    rowHover?: IGrowthOverviewRowHoverProps;
    showPeriodHeatmap: boolean;
    highlighted?: boolean;
}

const resolveHeatmapStyle = (
    pop: number | null | undefined,
    getHeatmapStyle: ((pop: number | null | undefined) => IGrowthHeatmapStyle) | undefined,
    getHeatmapColor: (pop: number | null | undefined) => string,
): IGrowthHeatmapStyle => {
    if (getHeatmapStyle) {
        return getHeatmapStyle(pop);
    }

    return {
        backgroundColor: getHeatmapColor(pop),
        shouldUseLightText: shouldUseLightHeatmapText(getHeatmapColor(pop)),
    };
};

const GrowthOverviewPeriodHeatmapCell = ({
    row,
    period,
    metric,
    granularity,
    getHeatmapColor,
    getHeatmapStyle,
    showPeriodHeatmap,
    highlighted,
    rowHover,
    cell,
}: IGrowthOverviewPeriodCellParams & { period: IGrowthOverviewPeriodCell; cell?: IGrowthOverviewPeriodCell }) => {
    const getHeatmapStyleFromContext = use(GrowthOverviewHeatmapContext);
    const isWeeklyCategoryUniqueUnavailable = isCategoryWeeklyUniqueUnavailable(granularity, metric, row.rowType);
    const shouldShowPeriodHeatmap =
        showPeriodHeatmap && !isWeeklyCategoryUniqueUnavailable && cell?.isIncomplete !== true;
    const categoryWeeklyUniqueUnavailableTooltip = isWeeklyCategoryUniqueUnavailable
        ? translate(GROWTH_OVERVIEW_I18N_KEYS.tooltip.categoryWeeklyUniqueUnavailable)
        : undefined;
    const heatmapStyle = shouldShowPeriodHeatmap
        ? resolveHeatmapStyle(cell?.pop, getHeatmapStyle ?? getHeatmapStyleFromContext ?? undefined, getHeatmapColor)
        : undefined;
    const periodBackground = highlighted ? GROWTH_OVERVIEW_EXPANDED_ROW_HIGHLIGHT : undefined;

    return (
        <GrowthOverviewPeriodCell
            style={periodBackground ? { backgroundColor: periodBackground } : undefined}
            data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-cell-${row.id}-${period.key}`}
            data-heatmap={showPeriodHeatmap ? "on" : "off"}
            {...getRowHoverCellProps(rowHover, highlighted, row.id)}
        >
            <HeatmapPercentCell
                pop={isWeeklyCategoryUniqueUnavailable ? null : (cell?.pop ?? null)}
                absoluteValue={isWeeklyCategoryUniqueUnavailable ? null : (cell?.value ?? null)}
                periodDate={isWeeklyCategoryUniqueUnavailable ? undefined : cell?.date}
                granularity={granularity}
                metric={metric}
                showHeatmap={shouldShowPeriodHeatmap}
                isIncomplete={cell?.isIncomplete === true}
                infoTooltip={categoryWeeklyUniqueUnavailableTooltip}
                backgroundColor={heatmapStyle?.backgroundColor}
                shouldUseLightText={heatmapStyle?.shouldUseLightText ?? false}
            />
        </GrowthOverviewPeriodCell>
    );
};

const renderPeriodCells = ({
    row,
    periodHeaders,
    metric,
    ...periodParams
}: IGrowthOverviewPeriodCellParams): ReactNode[] => {
    const periodsByKey = new Map(getPeriodsForMetric(row, metric).map((period) => [period.key, period]));

    return periodHeaders.map((period) => (
        <GrowthOverviewPeriodHeatmapCell
            key={`${row.id}-${period.key}`}
            row={row}
            period={period}
            periodHeaders={periodHeaders}
            metric={metric}
            cell={periodsByKey.get(period.key)}
            {...periodParams}
        />
    ));
};

const DomainRowName = ({
    row,
    searchHighlightTerm,
}: {
    row: IGrowthOverviewTableRow;
    searchHighlightTerm?: string;
}) => {
    const label = row.displayName;
    const domainHref = useGrowthOverviewDomainHref(row.domain);
    const highlightedLabel = (
        <GrowthOverviewHighlightedText
            text={label}
            searchHighlightTerm={searchHighlightTerm}
        />
    );

    return (
        <Flex
            alignItems="center"
            gap="8px"
        >
            {row.favicon ? (
                <img
                    src={row.favicon}
                    alt=""
                    width={16}
                    height={16}
                />
            ) : null}
            {domainHref ? (
                <WebsiteTooltip
                    domain={row.domain!}
                    hideTrackButton
                >
                    <StyledLink
                        href={domainHref}
                        data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-domain-link-${row.domain}`}
                    >
                        <Text
                            fontVariant="input2"
                            as="span"
                        >
                            {highlightedLabel}
                        </Text>
                    </StyledLink>
                </WebsiteTooltip>
            ) : (
                <Text fontVariant="input2">{highlightedLabel}</Text>
            )}
        </Flex>
    );
};

export interface IGroupRowCellsParams extends Omit<
    IGrowthOverviewPeriodCellParams,
    "showPeriodHeatmap" | "highlighted"
> {
    isExpanded: boolean;
    isLoadingBranch?: boolean;
    onToggle: (row: IGrowthOverviewTableRow) => void;
    nestDepth?: number;
}

export const renderGroupRowCells = ({
    row,
    isExpanded,
    isLoadingBranch = false,
    onToggle,
    nestDepth = 0,
    searchHighlightTerm,
    rowHover,
    ...periodParams
}: IGroupRowCellsParams): ReactNode[] => {
    const isExpandable = isGrowthOverviewRowExpandable(row);
    const summaryValue = getSummaryForMetric(row, periodParams.metric);
    const categoryWeeklyUniqueUnavailable = isCategoryWeeklyUniqueUnavailable(
        periodParams.granularity,
        periodParams.metric,
        row.rowType,
    );
    const categoryWeeklyUniqueUnavailableTooltip = categoryWeeklyUniqueUnavailable
        ? translate(GROWTH_OVERVIEW_I18N_KEYS.tooltip.categoryWeeklyUniqueUnavailable)
        : undefined;
    const groupRowType = row.rowType === "industry" ? "industry" : "sub-industry";
    const rowHoverCellProps = getRowHoverCellProps(rowHover, isExpanded, row.id);
    const nameCellContent = (
        <>
            {isExpandable ? (
                <ExpandIcon data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-row-expand-${row.id}`}>
                    {isLoadingBranch ? (
                        <GrowthOverviewExpandSpinner />
                    ) : (
                        <NavBarIcon
                            iconName={isExpanded ? "chev-down" : "chev-right"}
                            iconSize="xs"
                            iconColor={colorsPalettes.carbon[400]}
                        />
                    )}
                </ExpandIcon>
            ) : (
                <ExpandIcon />
            )}
            <GrowthOverviewRowLabel
                $rowType={groupRowType}
                fontVariant="input2"
            >
                <GrowthOverviewHighlightedText
                    text={row.displayName}
                    searchHighlightTerm={searchHighlightTerm}
                />
            </GrowthOverviewRowLabel>
        </>
    );

    return [
        isExpandable ? (
            <GrowthOverviewNameCellButton
                key={`${row.id}-name`}
                type="button"
                $nestDepth={nestDepth}
                $highlighted={isExpanded}
                onClick={() => onToggle(row)}
                data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-row-name-${row.id}`}
                {...rowHoverCellProps}
            >
                {nameCellContent}
            </GrowthOverviewNameCellButton>
        ) : (
            <GrowthOverviewNameCell
                key={`${row.id}-name`}
                $nestDepth={nestDepth}
                $highlighted={isExpanded}
                data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-row-name-${row.id}`}
                {...rowHoverCellProps}
            >
                {nameCellContent}
            </GrowthOverviewNameCell>
        ),
        <GrowthOverviewSummaryCell
            key={`${row.id}-summary`}
            $alignEnd
            $highlighted={isExpanded}
            {...rowHoverCellProps}
        >
            <GrowthOverviewRowLabel
                $rowType={groupRowType}
                fontVariant="input2"
            >
                {categoryWeeklyUniqueUnavailable ? (
                    <CategoryWeeklyUniqueUnavailableDash tooltip={categoryWeeklyUniqueUnavailableTooltip!} />
                ) : summaryValue == null ? (
                    NA_DASH
                ) : (
                    minVisitsAbbrFilter()(summaryValue)
                )}
            </GrowthOverviewRowLabel>
        </GrowthOverviewSummaryCell>,
        ...renderPeriodCells({
            ...periodParams,
            row,
            showPeriodHeatmap: !isExpanded,
            highlighted: isExpanded,
            rowHover,
        }),
    ];
};

interface IBranchLoadingRowCellsParams {
    rowId: string;
    periodHeaders: IGrowthOverviewPeriodCell[];
    nestDepth?: number;
}

export const renderBranchLoadingRowCells = ({
    rowId,
    periodHeaders,
    nestDepth = 0,
}: IBranchLoadingRowCellsParams): ReactNode[] => [
    <GrowthOverviewNameCell
        key={`${rowId}-loading-name`}
        $nestDepth={nestDepth}
        data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-row-loading-${rowId}`}
    >
        <ExpandIcon />
        <GrowthOverviewExpandSpinner />
    </GrowthOverviewNameCell>,
    <GrowthOverviewSummaryCell
        key={`${rowId}-loading-summary`}
        $alignEnd
    />,
    ...periodHeaders.map((period) => <GrowthOverviewPeriodCell key={`${rowId}-loading-${period.key}`} />),
];

export const renderDomainRowCells = (
    params: Omit<IGrowthOverviewPeriodCellParams, "showPeriodHeatmap" | "highlighted"> & {
        nestDepth?: number;
    },
): ReactNode[] => {
    const { row, nestDepth = 0, searchHighlightTerm, rowHover } = params;
    const summaryValue = getSummaryForMetric(row, params.metric);
    const rowHoverCellProps = getRowHoverCellProps(rowHover, false, row.id);

    return [
        <GrowthOverviewNameCell
            key={`${row.id}-name`}
            $nestDepth={nestDepth}
            data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-row-name-${row.id}`}
            {...rowHoverCellProps}
        >
            <DomainRowName
                row={row}
                searchHighlightTerm={searchHighlightTerm}
            />
        </GrowthOverviewNameCell>,
        <GrowthOverviewSummaryCell
            key={`${row.id}-summary`}
            $alignEnd
            {...rowHoverCellProps}
        >
            <Text fontVariant="input2">{summaryValue == null ? NA_DASH : minVisitsAbbrFilter()(summaryValue)}</Text>
        </GrowthOverviewSummaryCell>,
        ...renderPeriodCells({
            ...params,
            showPeriodHeatmap: true,
            rowHover,
        }),
    ];
};

interface IShowAllDomainsRowCellsParams {
    rowId: string;
    periodHeaders: IGrowthOverviewPeriodCell[];
    label: string;
    nestDepth?: number;
    onShowAll: () => void;
    rowHover?: IGrowthOverviewRowHoverProps;
}

export const renderShowAllDomainsRowCells = ({
    rowId,
    periodHeaders,
    label,
    nestDepth = 0,
    onShowAll,
    rowHover,
}: IShowAllDomainsRowCellsParams): ReactNode[] => {
    const rowHoverCellProps = getRowHoverCellProps(rowHover, false, rowId);

    return [
        <GrowthOverviewShowAllCellButton
            key={`${rowId}-show-all-name`}
            type="button"
            $nestDepth={nestDepth}
            onClick={onShowAll}
            data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-show-all-${rowId}`}
            {...rowHoverCellProps}
        >
            <ShowAllDomainsLabel fontVariant="input2">{label}</ShowAllDomainsLabel>
        </GrowthOverviewShowAllCellButton>,
        <GrowthOverviewSummaryCell
            key={`${rowId}-show-all-summary`}
            $alignEnd
            {...rowHoverCellProps}
        />,
        ...periodHeaders.map((period) => (
            <GrowthOverviewPeriodCell
                key={`${rowId}-show-all-${period.key}`}
                {...rowHoverCellProps}
            />
        )),
    ];
};
